import { useEffect } from "react";
import { useRoute, useLocation, Link } from "wouter";
import { Loader2, ArrowLeft, FileText } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { usePolicy, useInsuredByName } from "@/lib/api";

// The standalone `/policies/:id` route no longer renders its own page.
// Policy detail now lives in a single place — the insured detail page's
// Policies tab. This component resolves the policy's insured and redirects
// there (`/insureds/:insuredId?tab=policies&policyId=:policyId`), so every
// existing link to `/policies/:id` keeps working through one chokepoint.
export default function PolicyDetailRedirect() {
  const [, params] = useRoute("/policies/:id");
  const policyId = params?.id ?? "";
  const [, navigate] = useLocation();

  const { data: policy, isLoading, isError } = usePolicy(policyId);

  // Orphan policies (no FK to an insured) fall back to a name lookup, the
  // same resolution the legacy page used.
  const nameToResolve = policy && !policy.insuredId ? (policy.customerName ?? "") : "";
  const { data: insuredByName, isLoading: nameLoading } = useInsuredByName(nameToResolve);

  const insuredId = policy?.insuredId ?? insuredByName?.id ?? null;

  useEffect(() => {
    if (!insuredId) return;
    navigate(`/insureds/${insuredId}?tab=policies&policyId=${policyId}`, {
      replace: true,
    });
  }, [insuredId, policyId, navigate]);

  const resolving = isLoading || (!!policy && !policy.insuredId && nameLoading);

  if (resolving || insuredId) {
    return (
      <Layout fullWidth>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  // Policy missing, or its insured can't be resolved — nothing to redirect to.
  return (
    <Layout fullWidth>
      <div className="p-6">
        <Link href="/policies">
          <Button variant="ghost" className="gap-2 pl-0 hover:pl-2 transition-all">
            <ArrowLeft className="w-4 h-4" /> Back to Policies
          </Button>
        </Link>
        <Card className="mt-6">
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-lg font-medium">Policy not found</p>
            <p className="text-sm text-muted-foreground mt-1">
              {isError
                ? "This policy could not be loaded."
                : "This policy is no longer linked to an insured."}
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
